// Ventes Flash - Page publique pour les ACHETEURS
// Intégrée avec la navigation Marketplace standard
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Zap, ShoppingCart, Package, ChevronRight, Clock, Eye,
  Truck, Shield, Star, Tag, Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

import MainNavigation from '../components/layout/MainNavigation';
import UnifiedHeader from '../components/layout/UnifiedHeader';
import { useBuyer } from '../contexts/BuyerContext';

// Countdown Timer Component
function FlashCountdown({ endDate, size = 'md' }) {
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const end = new Date(endDate).getTime();
      const diff = end - now;

      if (diff > 0) {
        setTimeLeft({
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000)
        });
      } else {
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [endDate]);

  const sizeClasses = size === 'lg' 
    ? 'text-lg px-3 py-2' 
    : 'text-sm px-2 py-1';

  return (
    <div className="flex items-center gap-1 font-mono">
      <span className={`bg-black text-white ${sizeClasses} rounded font-bold`}>
        {String(timeLeft.hours).padStart(2, '0')}
      </span>
      <span className="text-gray-600 font-bold">:</span>
      <span className={`bg-black text-white ${sizeClasses} rounded font-bold`}>
        {String(timeLeft.minutes).padStart(2, '0')}
      </span>
      <span className="text-gray-600 font-bold">:</span>
      <span className={`bg-black text-white ${sizeClasses} rounded font-bold`}>
        {String(timeLeft.seconds).padStart(2, '0')}
      </span>
    </div>
  );
}

// Carte Vente Flash - Mise à jour avec bouton "Voir le produit"
function FlashSaleCard({ sale, onAddToCart }) {
  const navigate = useNavigate();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 hover:shadow-xl transition-all"
    >
      {/* Header avec countdown */}
      <div className="bg-gradient-to-r from-red-600 to-orange-500 px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap size={16} className="text-yellow-300 fill-yellow-300" />
          <span className="text-white text-xs font-bold">Se termine dans</span>
        </div>
        <FlashCountdown endDate={sale.endDate} />
      </div>

      {/* Image produit */}
      <Link to={`/produit/${sale.product.id}?flash=${sale.id}`} className="block">
        <div className="relative aspect-square bg-gray-50 cursor-pointer group overflow-hidden">
          {sale.product.image ? (
            <img 
              src={sale.product.image} 
              alt={sale.product.title} 
              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <Package size={48} className="text-gray-300" />
            </div>
          )}
          {/* Badge réduction */}
          <div className="absolute top-3 left-3 bg-red-600 text-white px-2.5 py-1 rounded-full text-sm font-bold shadow-lg">
            -{sale.discount}%
          </div>
          {/* Overlay hover */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
            <span className="opacity-0 group-hover:opacity-100 bg-white/90 text-gray-900 px-4 py-2 rounded-full font-medium text-sm transition-opacity">
              Voir le produit
            </span>
          </div>
        </div>
      </Link>

      {/* Infos produit */}
      <div className="p-4">
        <Link to={`/produit/${sale.product.id}?flash=${sale.id}`}>
          <h3 className="font-semibold text-gray-900 line-clamp-2 mb-2 min-h-[44px] hover:text-orange-600 transition-colors">
            {sale.product.title}
          </h3>
        </Link>

        {/* Prix */}
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-2xl font-bold text-red-600">{sale.flashPrice}€</span>
          <span className="text-base text-gray-400 line-through">{sale.product.originalPrice}€</span>
        </div>

        {/* Économie */}
        <div className="bg-green-50 border border-green-200 rounded-lg px-2.5 py-1.5 mb-3">
          <p className="text-xs text-green-700 font-medium">
            Tu économises {(sale.product.originalPrice - sale.flashPrice).toFixed(2)}€
          </p>
        </div>

        {/* Stock */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-gray-500">Stock disponible</span>
            <span className="font-bold text-orange-600">{sale.stock} restants</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: `${(sale.stock / sale.initialStock) * 100}%` }}
              className="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full"
            />
          </div>
        </div>

        {/* Boutons */}
        <div className="flex gap-2">
          <Button 
            onClick={() => navigate(`/produit/${sale.product.id}?flash=${sale.id}`)}
            variant="outline"
            className="flex-1 border-gray-300 hover:border-orange-500 hover:text-orange-600 font-medium py-2.5 rounded-xl flex items-center justify-center gap-1.5"
          >
            <Eye size={16} />
            Voir
          </Button>
          <Button 
            onClick={() => onAddToCart(sale)}
            className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-2.5 rounded-xl flex items-center justify-center gap-1.5"
          >
            <ShoppingCart size={16} />
            Ajouter
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

export default function VentesFlashPublicPage() {
  const navigate = useNavigate();
  const { addToCart } = useBuyer();
  const [flashSales, setFlashSales] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFlashSales();
  }, []);

  const fetchFlashSales = async () => {
    setLoading(true);
    try {
      // Données simulées avec images
      const now = new Date();
      setFlashSales([
        {
          id: 'flash-1',
          product: { 
            id: 'demo-product-1', 
            title: 'Perceuse Bosch Professional GSB 18V-55 - Batterie et chargeur inclus', 
            originalPrice: 149, 
            image: 'https://images.unsplash.com/photo-1770763233593-74dfd0da7bf0?w=400' 
          },
          flashPrice: 99,
          discount: 34,
          stock: 8,
          initialStock: 15,
          endDate: new Date(now.getTime() + 6 * 60 * 60 * 1000).toISOString(),
        },
        {
          id: 'flash-2',
          product: { 
            id: 'demo-product-3', 
            title: 'Meuleuse angulaire Makita 125mm - 720W avec disque diamant', 
            originalPrice: 79, 
            image: 'https://images.pexels.com/photos/13792037/pexels-photo-13792037.jpeg?auto=compress&w=400' 
          },
          flashPrice: 49,
          discount: 38,
          stock: 12,
          initialStock: 20,
          endDate: new Date(now.getTime() + 12 * 60 * 60 * 1000).toISOString(),
        },
        {
          id: 'flash-3',
          product: { 
            id: 'demo-product-5', 
            title: 'Niveau laser Bosch GLL 2-10 Professional - Portée 10m', 
            originalPrice: 129, 
            image: 'https://images.pexels.com/photos/6474119/pexels-photo-6474119.jpeg?auto=compress&w=400' 
          },
          flashPrice: 89,
          discount: 31,
          stock: 5,
          initialStock: 10,
          endDate: new Date(now.getTime() + 3 * 60 * 60 * 1000).toISOString(),
        },
        {
          id: 'flash-4',
          product: { 
            id: 'demo-product-2', 
            title: 'Coffret tournevis Facom 10 pièces - Embouts magnétiques', 
            originalPrice: 89, 
            image: 'https://images.pexels.com/photos/5691656/pexels-photo-5691656.jpeg?auto=compress&w=400' 
          },
          flashPrice: 59,
          discount: 34,
          stock: 3,
          initialStock: 8,
          endDate: new Date(now.getTime() + 2 * 60 * 60 * 1000).toISOString(),
        },
        {
          id: 'flash-5',
          product: { 
            id: 'demo-product-4', 
            title: 'Scie circulaire Bosch GKS 190 - 1400W lame 190mm', 
            originalPrice: 159, 
            image: 'https://images.pexels.com/photos/30871012/pexels-photo-30871012.jpeg?auto=compress&w=400' 
          },
          flashPrice: 109,
          discount: 31,
          stock: 4,
          initialStock: 10,
          endDate: new Date(now.getTime() + 5 * 60 * 60 * 1000).toISOString(),
        },
        {
          id: 'flash-6',
          product: { 
            id: 'demo-product-6', 
            title: 'Tableau électrique 2 rangées Legrand - Pré-câblé', 
            originalPrice: 119, 
            image: 'https://images.pexels.com/photos/5767595/pexels-photo-5767595.jpeg?auto=compress&w=400' 
          },
          flashPrice: 79,
          discount: 34,
          stock: 6,
          initialStock: 12,
          endDate: new Date(now.getTime() + 8 * 60 * 60 * 1000).toISOString(),
        },
        {
          id: 'flash-7',
          product: { 
            id: 'demo-product-11', 
            title: 'Mitigeur thermostatique douche Grohe Precision', 
            originalPrice: 189, 
            image: 'https://images.unsplash.com/photo-1761353855019-05f2f3ed9c43?w=400' 
          },
          flashPrice: 129,
          discount: 32,
          stock: 7,
          initialStock: 15,
          endDate: new Date(now.getTime() + 4 * 60 * 60 * 1000).toISOString(),
        },
        {
          id: 'flash-8',
          product: { 
            id: 'demo-product-16', 
            title: 'Peinture acrylique blanc mat Tollens 10L - Murs & plafonds', 
            originalPrice: 69, 
            image: 'https://images.unsplash.com/photo-1621685682093-3b8016dcb57d?w=400' 
          },
          flashPrice: 45,
          discount: 35,
          stock: 15,
          initialStock: 30,
          endDate: new Date(now.getTime() + 10 * 60 * 60 * 1000).toISOString(),
        },
      ]);
    } catch (e) {
      console.error('Error fetching flash sales:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (sale) => {
    addToCart({
      id: sale.product.id,
      name: sale.product.title,
      price: sale.flashPrice,
      image: sale.product.image,
      quantity: 1,
      isFlashSale: true,
      flashSaleId: sale.id,
      originalPrice: sale.product.originalPrice
    });
    toast.success(`${sale.product.title.substring(0, 30)}... ajouté au panier !`);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="ventes-flash-public-page">
      {/* Header Global */}
      <UnifiedHeader />
      
      {/* Navigation Marketplace */}
      <MainNavigation />

      {/* Spacer pour compenser les headers fixed */}
      <div className="h-11 md:hidden" />
      <div className="hidden md:block" style={{ height: '148px' }} />

      {/* Hero Banner - Plus compact */}
      <div className="bg-gradient-to-r from-red-600 via-orange-500 to-red-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)'
          }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 py-8 md:py-10 relative">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <Zap size={32} className="text-yellow-300 fill-yellow-300 animate-pulse" />
              <h1 className="text-3xl md:text-4xl font-black text-white">VENTES FLASH</h1>
              <Zap size={32} className="text-yellow-300 fill-yellow-300 animate-pulse" />
            </div>
            <p className="text-white/90 text-base mb-4">
              Offres exceptionnelles à durée limitée ! Jusqu'à -40% sur l'outillage pro
            </p>
            <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur rounded-full px-5 py-2.5">
              <span className="text-white font-medium text-sm">Prochaine fin d'offre dans</span>
              <FlashCountdown endDate={flashSales[0]?.endDate || new Date()} size="lg" />
            </div>
          </div>
        </div>
      </div>

      {/* Badges de confiance */}
      <div className="bg-white border-b border-gray-200 py-3">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center gap-6 md:gap-8 flex-wrap">
            {[
              { icon: Zap, text: 'Sans intermédiaire' },
              { icon: Tag, text: 'Zéro commission' },
              { icon: Shield, text: 'Paiement sécurisé' },
              { icon: Award, text: 'Vendeur certifié' },
              { icon: Star, text: 'Avis vérifiés' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-gray-600">
                <item.icon size={18} className="text-orange-500" />
                <span className="text-xs md:text-sm font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <main className="flex-1 max-w-7xl mx-auto px-4 py-6 w-full">
        {/* Compteur de ventes */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">
              {flashSales.length} offres disponibles
            </h2>
            <p className="text-gray-500 text-sm">Dépêche-toi, les stocks sont limités !</p>
          </div>
          <Link 
            to="/produits"
            className="text-orange-600 hover:text-orange-700 font-medium flex items-center gap-1 text-sm"
          >
            Voir tous les produits <ChevronRight size={16} />
          </Link>
        </div>

        {/* Grille des ventes flash - 4 par ligne sur desktop */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {flashSales.map((sale) => (
              <FlashSaleCard
                key={sale.id}
                sale={sale}
                onAddToCart={handleAddToCart}
              />
            ))}
          </div>
        )}

        {/* CTA Newsletter */}
        <div className="mt-10 bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-6 md:p-8 text-center">
          <Zap size={28} className="text-yellow-400 mx-auto mb-3" />
          <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
            Ne rate plus aucune vente flash !
          </h3>
          <p className="text-gray-400 mb-5 text-sm">
            Inscris-toi pour être alerté dès qu'une nouvelle offre est disponible
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input 
              type="email"
              placeholder="Ton email"
              className="flex-1 px-4 py-2.5 rounded-xl border-0 focus:ring-2 focus:ring-orange-500 text-sm"
            />
            <Button className="bg-gradient-to-r from-orange-500 to-red-500 hover:opacity-90 text-white font-bold px-5 py-2.5 rounded-xl text-sm">
              M'alerter
            </Button>
          </div>
        </div>
      </main>

      {/* Footer Global */}
      
    </div>
  );
}
